import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { message } from 'antd';
import { GoodunclesService, ImageRef } from '@gooduncles/gu-app-schema';

import { IMAGE_BUCKET } from 'src/constants/storage.constants';
import { uploadImageWithMetadata } from 'src/utils/image-util';

import TiptapImageExtension from './TiptapImageExtension';
import EditorToolbar from './EditorToolbar';

import styles from './CommerceNoticeEditor.module.scss';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

interface CommerceNoticeEditorProps {
  content: Record<string, any> | null;
  images: Record<string, ImageRef>;
  onContentChange: (content: Record<string, any>) => void;
  onImagesChange: (images: Record<string, ImageRef>) => void;
  noticeId: string;
  authorId: string;
}

const CommerceNoticeEditor: FC<CommerceNoticeEditorProps> = ({
  content,
  images,
  onContentChange,
  onImagesChange,
  noticeId,
  authorId,
}) => {
  const [imageUploading, setImageUploading] = useState(false);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Placeholder.configure({
        placeholder: '공지사항 내용을 입력하세요...',
      }),
      TiptapImageExtension,
    ],
    content: content ?? undefined,
    onUpdate: ({ editor }) => {
      onContentChange(editor.getJSON());
    },
  });

  // 수정 모드: 비동기로 로드된 content를 에디터에 반영
  const initialContentLoaded = useRef(false);
  useEffect(() => {
    if (editor && content && !initialContentLoaded.current) {
      editor.commands.setContent(content);
      initialContentLoaded.current = true;
    }
  }, [editor, content]);

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!VALID_IMAGE_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE) {
        message.error('유효하지 않은 이미지 파일입니다. (JPG/PNG, 5MB 이하)');
        return;
      }

      setImageUploading(true);
      try {
        const result = await uploadImageWithMetadata(IMAGE_BUCKET, file, {
          service: GoodunclesService.Commerce,
          uploaderId: authorId,
          owner: { entity: 'commerceNotice', noticeId },
        });

        const currentImages = imagesRef.current;
        const newImages: Record<string, ImageRef> = {
          ...currentImages,
          [result.imageFileId]: {
            originalUrl: result.result.originalUrl,
            thumbnailUrl: result.result.thumbnailUrl,
            index: Object.keys(currentImages).length,
          },
        };
        imagesRef.current = newImages;
        onImagesChange(newImages);

        editor
          ?.chain()
          .focus()
          .setImage({
            imageFileId: result.imageFileId,
            src: result.result.originalUrl,
          })
          .run();
        message.success('이미지가 업로드되었습니다.');
      } catch (error) {
        console.error('Image upload failed:', error);
        message.error('이미지 업로드에 실패했습니다.');
      } finally {
        setImageUploading(false);
      }
    },
    [editor, onImagesChange, noticeId, authorId],
  );

  return (
    <div className={styles.editorWrapper}>
      <EditorToolbar editor={editor} onImageUpload={handleImageUpload} imageUploading={imageUploading} />
      <div className={styles.editorContent}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default CommerceNoticeEditor;
